import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { Steps, Button } from 'antd';
import DrawerLayout from '../shared/DrawerLayout';
import {
  getIsVisibleModal,
  closeModal,
  openConfirmModal,
} from '../../redux/store/ui/common';
import { fetchUsers } from '../../redux/store/userData/users';
import { getBulkAction, ExecuteResult, extractColumn } from './registry';
import UploadStep, { ParsedCsv } from './steps/UploadStep';
import PreviewStep from './steps/PreviewStep';
import ExecuteStep from './steps/ExecuteStep';
import { BULK_ARCHIVE_DRAWER, BULK_ARCHIVE_DRAWER_WIDTH } from './constants';
import { drawerStyles } from './BulkActions.styles';
import { makeT } from './messages';
import type { IdentifierKey } from './registry';

const BulkArchiveDrawer: React.FC = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const t = makeT(intl);
  const visible = useSelector(getIsVisibleModal(BULK_ARCHIVE_DRAWER));
  const config = getBulkAction('archiveUsers')!;

  const [step, setStep] = useState(0);
  const [parsed, setParsed] = useState<ParsedCsv | null>(null);
  const [identifierKey, setIdentifierKey] = useState<IdentifierKey>('userId');
  const [resolving, setResolving] = useState(false);
  const [matched, setMatched] = useState<any[]>([]);
  const [unmatched, setUnmatched] = useState<string[]>([]);
  const [selectedRecords, setSelectedRecords] = useState<any[]>([]);
  const [executing, setExecuting] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [result, setResult] = useState<ExecuteResult | null>(null);

  // Derived: which column we'll actually read from the CSV, based on the
  // current "Filter by" choice. colIndex === -1 means the CSV has no header
  // matching the chosen filter — UploadStep surfaces the error and Next is
  // disabled.
  const selection = useMemo(() => {
    if (!parsed) return { colIndex: -1, values: [] };
    const target =
      config.idTargets.find((t) => t.key === identifierKey) ||
      config.idTargets[0];
    return extractColumn(parsed.header, parsed.rows, target);
  }, [parsed, identifierKey, config]);

  useEffect(() => {
    document.body.classList.toggle('ba-drawer-open', visible);
    return () => document.body.classList.remove('ba-drawer-open');
  }, [visible]);

  const reset = () => {
    setStep(0);
    setParsed(null);
    setIdentifierKey('userId');
    setResolving(false);
    setMatched([]);
    setUnmatched([]);
    setSelectedRecords([]);
    setExecuting(false);
    setProgress({ done: 0, total: 0 });
    setResult(null);
  };

  const handleClose = () => {
    dispatch(closeModal(BULK_ARCHIVE_DRAWER));
    reset();
  };

  const handleDone = () => {
    dispatch(fetchUsers(0, 50, () => {}));
    handleClose();
  };

  const goToPreview = async () => {
    if (!parsed || selection.colIndex < 0 || selection.values.length === 0) {
      return;
    }
    setStep(1);
    setResolving(true);
    const target =
      config.idTargets.find((t) => t.key === identifierKey) ||
      config.idTargets[0];
    const { matched: m, unmatched: u } = await config.resolve(
      selection.values,
      target
    );
    setMatched(m);
    setUnmatched(u);
    setSelectedRecords(m);
    setResolving(false);
  };

  const startExecute = async () => {
    setStep(2);
    setExecuting(true);
    setProgress({ done: 0, total: selectedRecords.length });
    const res = await config.execute(selectedRecords, (done, total) =>
      setProgress({ done, total })
    );
    setResult(res);
    setExecuting(false);
  };

  const confirmArchive = () => {
    dispatch(
      openConfirmModal({
        destructive: true,
        title: t('fleetadmin.bulk.confirm.title', {
          count: selectedRecords.length,
        }),
        onConfirm: startExecute,
      })
    );
  };

  const stepItems = [
    { title: t('fleetadmin.bulk.step.upload') },
    { title: t('fleetadmin.bulk.step.preview') },
    { title: t('fleetadmin.bulk.step.confirm') },
  ];

  const renderFooter = () => {
    if (step === 0) {
      return (
        <>
          <span className="ba-footer-info">
            {parsed
              ? t('fleetadmin.bulk.footer.rows', {
                  count: selection.values.length,
                })
              : ''}
          </span>
          <div className="ba-footer-actions">
            <Button onClick={handleClose}>
              {t('fleetadmin.bulk.footer.cancel')}
            </Button>
            <Button
              type="primary"
              disabled={
                !parsed ||
                selection.colIndex < 0 ||
                selection.values.length === 0
              }
              onClick={goToPreview}
              data-testid="BulkArchive-next"
            >
              {t('fleetadmin.bulk.footer.next')}
            </Button>
          </div>
        </>
      );
    }
    if (step === 1) {
      return (
        <>
          <span className="ba-footer-info">
            {t('fleetadmin.bulk.footer.selected', {
              selected: selectedRecords.length,
              total: matched.length,
            })}
          </span>
          <div className="ba-footer-actions">
            <Button onClick={() => setStep(0)}>
              {t('fleetadmin.bulk.footer.back')}
            </Button>
            <Button onClick={handleClose}>
              {t('fleetadmin.bulk.footer.cancel')}
            </Button>
            <Button
              danger
              type="primary"
              disabled={resolving || selectedRecords.length === 0}
              onClick={confirmArchive}
              data-testid="BulkArchive-archive"
            >
              {t('fleetadmin.bulk.footer.archive', {
                count: selectedRecords.length,
              })}
            </Button>
          </div>
        </>
      );
    }
    return (
      <>
        <span className="ba-footer-info" />
        <div className="ba-footer-actions">
          <Button disabled={executing} onClick={reset}>
            {t('fleetadmin.bulk.footer.runAnother')}
          </Button>
          <Button
            type="primary"
            disabled={executing}
            onClick={handleDone}
            data-testid="BulkArchive-done"
          >
            {t('fleetadmin.bulk.footer.done')}
          </Button>
        </div>
      </>
    );
  };

  const body = (
    <div className="ba-drawer-body">
      <div className="ba-steps">
        <Steps current={step} items={stepItems} />
      </div>

      <div className="ba-step">
        {step === 0 && (
          <UploadStep
            config={config}
            parsed={parsed}
            setParsed={setParsed}
            identifierKey={identifierKey}
            setIdentifierKey={setIdentifierKey}
            selection={selection}
          />
        )}
        {step === 1 && (
          <PreviewStep
            resolving={resolving}
            matched={matched}
            unmatched={unmatched}
            parsed={parsed}
            selection={selection}
            selectedRecords={selectedRecords}
            setSelectedRecords={setSelectedRecords}
          />
        )}
        {step === 2 && (
          <ExecuteStep
            executing={executing}
            progress={progress}
            result={result}
          />
        )}
      </div>

      <style jsx global>
        {drawerStyles}
      </style>
    </div>
  );

  return (
    <DrawerLayout
      visible={visible}
      width={BULK_ARCHIVE_DRAWER_WIDTH}
      title={t(config.labelId)}
      body={body}
      footer={<div className="ba-footer">{renderFooter()}</div>}
      onReload={undefined}
      onClose={handleClose}
    />
  );
};

export default BulkArchiveDrawer;
